import Polygon from "ol/geom/Polygon"
import type OlMap from "ol/Map"
import { fromLonLat } from "ol/proj"

export const useOpenLayersCenter = (
  map: Ref<OlMap | null>,
  clusterZoomLevel: number,
  bounds: Ref<MapBounds>,
  forceInteract: () => void,
  restoreLastView: () => boolean,
  refitAfterLoadOfCluster: Ref<boolean>,
  zoom: Ref<number | undefined>,
) => {
  const initializeRestore = ref(false)

  const fitMapToVisibleMarkers = (forCluster?: boolean, clusters?: MaybeRef<Array<GeoCluster> | undefined>) => {
    if (!map.value) return

    const view = map.value.getView()

    if (!initializeRestore.value) {
      const succesFullyWentToLastView = restoreLastView()
      initializeRestore.value = true

      if (succesFullyWentToLastView) {
        const newZoom = view.getZoom() ?? 0

        if (forCluster && newZoom > clusterZoomLevel) {
          forceInteract()
        }

        return
      }
    }

    // Convert the bounds to an extent
    const extent = fromLonLat([bounds.value.west, bounds.value.south]).concat(fromLonLat([bounds.value.east, bounds.value.north]))
    const isSinglePoint = bounds.value.east === bounds.value.west && bounds.value.north === bounds.value.south

    // Verify the extent is valid
    if (!Array.isArray(extent) || extent.length !== 4) return
    const [minX, minY, maxX, maxY] = extent
    if (![minX, minY, maxX, maxY].every(Number.isFinite)) return
    if (minX! > maxX! || minY! > maxY!) return

    if (zoom.value) {
      view.setZoom(zoom.value)
    } else if (isSinglePoint) {
      clusters = unref(clusters)
      if (clusters?.length) {
        const coordinates = clusters.flatMap((cluster) => cluster.convexHull.map((coordinate) => fromLonLat(coordinate)))
        const geometry = new Polygon([coordinates])
        const padding = 25
        view.fit(geometry, { padding: [padding, padding, padding, padding] })
      } else {
        view.setCenter(extent)
        view.setZoom(clusterZoomLevel + 0.01)
      }
    } else {
      const padding = 50
      view.fit(extent, { padding: [padding, padding, padding, padding] })
    }

    const newZoom = view.getZoom() ?? 0

    // force interact emit if zoom is greater than cluster zoom level
    // since geo assets should be fetched as they should be shown
    if (forCluster && newZoom > clusterZoomLevel) {
      forceInteract()
      refitAfterLoadOfCluster.value = true
    }
  }

  return {
    fitMapToVisibleMarkers,
  }
}
